import { useState } from 'react';
import { X, Mail } from 'lucide-react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SharedUser {
  id: string;
  email: string;
  isOwner?: boolean;
}

export function ShareModal({ open, onOpenChange }: ShareModalProps) {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [sharedUsers, setSharedUsers] = useState<SharedUser[]>(() => {
    // Initialize with owner if user exists
    return user ? [{ id: user.id, email: user.email, isOwner: true }] : [];
  });

  const handleAddUser = () => {
    // Basic email validation
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      // toast.error('Please enter a valid email address');
      return;
    }

    // Check for duplicates
    if (sharedUsers.some(user => user.email === email)) {
      // toast.error('This email has already been added');
      return;
    }

    // Add new user
    setSharedUsers([
      ...sharedUsers,
      { id: crypto.randomUUID(), email, isOwner: false }
    ]);
    setEmail('');
    // toast.success('User added successfully');
  };

  const handleRemoveUser = (id: string) => {
    // Prevent removing the owner
    if (sharedUsers.find(user => user.id === id)?.isOwner) {
      // toast.error('Cannot remove the owner');
      return;
    }

    setSharedUsers(sharedUsers.filter(user => user.id !== id));
    // toast.success('User removed');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddUser();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-6 !pt-6 !px-6">
        <div className="space-y-3">
          <DialogTitle>Invite collaborators</DialogTitle>
          <p className="text-sm text-muted-foreground">Add team members to manage and collaborate</p>
        </div>
        
        <div className="mt-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Add an email id"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-9"
              />
            </div>
            <Button onClick={handleAddUser}>Add</Button>
          </div>

          <ScrollArea className="h-[240px] mt-4">
            <div className="divide-y divide-border">
              {sharedUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between h-12 px-3 transition-colors hover:bg-secondary/20 group"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{user.email}</span>
                    {user.isOwner && (
                      <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded">
                        Owner
                      </span>
                    )}
                  </div>
                  {!user.isOwner && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveUser(user.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {sharedUsers.length === 0 && (
                <div className="text-center h-12 flex items-center justify-center text-muted-foreground">
                  <p className="text-sm font-medium">No users added yet</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}